import {inject, Injectable} from '@angular/core';
import {Resolve} from '@angular/router';
import {firstValueFrom, from, Observable, Subscription} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';
import {destroy$} from '../destroyable';
import {DexStreamingService} from './dex-streaming/dex-streaming.service';
import {DexMdSubjectService} from './dex-streaming/subjects/dex-md-subject-service';
import {DexSecuritiesSubjectService} from './dex-streaming/subjects/dex-securities-subject-service';

@Injectable()
export class DexStreamingInitResolverService implements Resolve<void> {

    private readonly dexStreamingService = inject(DexStreamingService);
    private readonly dexSecuritiesSubjectService = inject(DexSecuritiesSubjectService);
    private readonly dexMdSubjectService = inject(DexMdSubjectService);

    private initProcess: Promise<void> | undefined = undefined;

    private readonly destroy$ = destroy$();

    private subscription: Subscription;

    public resolve(): Observable<void> {
        this.initProcess ??= this.waitForInitialData();
        this.initSubscriptions();

        return from(this.initProcess);
    }

    private async waitForInitialData(): Promise<void> {
        const deps: Record<string, Observable<unknown>> = {
            deliverables:    this.dexSecuritiesSubjectService.deliverablesSnapshotReceived$,
            tradables:       this.dexSecuritiesSubjectService.tradablesSnapshotReceived$,
            liquidityPools:  this.dexMdSubjectService.lpSnapshotReceived$,
            tranches:        this.dexMdSubjectService.tranchesSnapshotReceived$,
            positions:       this.dexMdSubjectService.positionsSnapshotReceived$,
            trancheDelRules: this.dexMdSubjectService.trancheDelRulesSnapshotReceived$,
            trancheRules:    this.dexMdSubjectService.trancheRulesSnapshotReceived$
        };

        const notResolved = new Set(Object.keys(deps));

        const TOO_LONG_WAITING_TIMEOUT_MS = 1000 * 5; // 5 sec
        const warnTimeoutId = setTimeout(() => {
            console.warn(
                `Waiting for snapshots for too long (${TOO_LONG_WAITING_TIMEOUT_MS / 1000} sec). ` +
                `Not yet resolved data: ${[...notResolved].join(', ')}`,
                notResolved
            );
        }, TOO_LONG_WAITING_TIMEOUT_MS);

        await Promise.all(
            Object.entries(deps).map(async ([key, stream$]) => {
                await firstValueFrom(stream$);
                notResolved.delete(key);
            })
        );

        clearTimeout(warnTimeoutId);
    }

    private initSubscriptions(): void {
        this.subscription ??= this.dexStreamingService.stream$.pipe(
            takeUntil(this.destroy$),
            finalize(() => this.subscription = undefined)
        ).subscribe();
    }
}
